import { FC } from 'react';
import { Dropdown, InlineNotification, Layer, TextInput } from '@carbon/react';

import type { EntityRef } from './types';

/** Props for {@link AssetEntityField}. */
export interface AssetEntityFieldProps {
  /** Field id — both asset modals render this, so the ids must stay distinct. */
  id: string;
  /** Entities the new asset may be filed under, from the Step 0 selection. */
  options: EntityRef[];
  /** The chosen entity, or null while nothing is chosen. */
  value: EntityRef | null;
  /** Fires with the picked entity's id. */
  onChange: (entityId: number) => void;
  /** Freeze the field while a submit is in flight. */
  disabled?: boolean;
}

/**
 * The "Entity" field at the top of Add Wellbore / Add LTD. A new asset is
 * filed under exactly one entity (`POST /entities/:entity_id/assets`), so
 * what this renders depends on how many the deal offers:
 *
 *   - one — read-only. There is nothing to decide.
 *   - several — a dropdown. Only the user knows whose asset this is, and the
 *     modal's Create button waits until they say.
 *   - none — an empty dropdown and the reason, which is read before the field
 *     because a disabled control cannot be tabbed to. None of the selected
 *     sources has a seller entity behind it.
 *
 * For a source that came from Salesforce the name shown is its CRM account's,
 * which can differ from the linked entity's — the id is what decides where
 * the asset lands.
 *
 * @param {AssetEntityFieldProps} props - Options, current value, change handler
 * @return {JSX.Element} The entity field
 */
export const AssetEntityField: FC<AssetEntityFieldProps> = ({
  id,
  options,
  value,
  onChange,
  disabled,
}) => {
  if (options.length === 1) {
    return (
      <Layer>
        <TextInput
          id={id}
          labelText="Entity"
          value={options[0].name}
          readOnly
        />
      </Layer>
    );
  }

  return (
    <>
      {/*
        Before the field, not after it. A disabled control is not in the tab
        order, so someone arriving by keyboard never lands on it and would meet
        the reason only after tabbing past the whole thing. Reading order is
        what carries the explanation here.
      */}
      {options.length === 0 && (
        <InlineNotification
          kind="info"
          title="No entity to file this asset under"
          subtitle="None of the selected sources has a seller entity behind it. Go back to Select Source and add one that does."
          hideCloseButton
          lowContrast
        />
      )}
      <Layer>
        <Dropdown
          id={id}
          titleText="Entity *"
          label="Choose an entity"
          items={options}
          itemToString={(item: EntityRef | null) => item?.name ?? ''}
          selectedItem={value}
          onChange={({ selectedItem }: { selectedItem: EntityRef | null }) => {
            if (selectedItem) onChange(selectedItem.id);
          }}
          disabled={disabled || options.length === 0}
        />
      </Layer>
    </>
  );
};
